import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax'
import { createContext, useContext, useMemo, useState } from 'react'
import { Scheduler, WeekView, DayView, Appointments, Toolbar, DateNavigator, TodayButton } from '@devexpress/dx-react-scheduler-material-ui'
import { ViewState } from '@devexpress/dx-react-scheduler'
import { useTranslation } from 'react-i18next'

import { Paper, Typography, Box, Stack, Chip, Divider, Tooltip, useTheme, useMediaQuery, ChipProps, CircularProgress, Alert, Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'

import { Shift, ShiftTemplate } from '@/core/models/shift.model'

import { CalendarShiftData, PlanningViewMode } from '../model/types'
import { ShiftAppointment } from './ShiftAppointment'
import { ShiftTemplateAppointment } from './ShiftTemplateAppointment'
import { usePlanningCalendar } from '../model/usePlanningCalendar'
import { useGetShiftExceptionsQuery, useGetShiftsQuery, useGetShiftTemplatesQuery } from '@/core/services/planning'
import { Planning } from '@/core/models/planning.model'
import { toAdapterLocale } from '@/utils/locale'

import { getPlanningWeekRange, PLANNING_WEEK_START_DAY } from '../lib/planningWeek'

const useStyles = makeStyles((theme: Theme) => ({
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  colorBox: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(0.5),
    borderRadius: 2,
  },
}))

dayjs.extend(minMax)

type PlanningCalendarProps = {
  planning: Planning
  viewMode: PlanningViewMode
}

type PlanningCalendarContextType = {
  planningId: string
  shifts: Shift[]
}

const PlanningCalendarContext = createContext<PlanningCalendarContextType | null>(null)

function usePlanningCalendarContext() {
  const context = useContext(PlanningCalendarContext)
  if (!context) {
    throw new Error('usePlanningCalendar must be used within PlanningCalendar')
  }
  return context
}

export function PlanningCalendar({ planning, viewMode }: PlanningCalendarProps) {
  const planningId = planning._id
  const { i18n } = useTranslation()
  const theme = useTheme()
  // On phones a 7-day week grid is unusable, so fall back to a single-day view.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const hideStatistics = useMediaQuery(theme.breakpoints.down('md'))

  // Shared between the mobile (DayView) and desktop (WeekView) scheduler views.
  const calendarViewProps = {
    startDayHour: 7,
    endDayHour: 20,
    timeScaleLayoutComponent: TimeScaleLayout,
    timeTableCellComponent: TimeTableCell,
    dayScaleCellComponent: DayScaleCell,
    timeScaleLabelComponent: TimeScaleLabel,
    cellDuration: 60,
  }
  // Open the calendar on a week that actually contains shifts: if the planning hasn't started
  // yet (e.g. it starts June 1st and today is May 31st) land on its start week, if it already
  // ended land on its end week, otherwise land on today. Without this the view defaults to the
  // current week and shows nothing for future- or past-bounded plannings.
  const [currentDate, setCurrentDate] = useState(() => {
    const today = dayjs()
    const start = dayjs(planning.startDate)
    const end = dayjs(planning.endDate)
    if (start.isValid() && today.isBefore(start, 'day')) return start.toDate()
    if (end.isValid() && today.isAfter(end, 'day')) return end.toDate()
    return today.toDate()
  })

  const { startDate: startDateOfWeek, endDate: endDateOfWeek } = getPlanningWeekRange(currentDate)

  const {
    data: shifts = [],
    refetch: refetchShifts,
    isLoading: isLoadingShifts,
    error: shiftsError,
  } = useGetShiftsQuery({ planningId, startDate: startDateOfWeek, endDate: endDateOfWeek }, { refetchOnMountOrArgChange: true })

  const { data: shiftExceptions = [], isLoading: isLoadingShiftExceptions } = useGetShiftExceptionsQuery(
    { planningId, dateFrom: startDateOfWeek, dateTo: endDateOfWeek },
    { refetchOnMountOrArgChange: true }
  )

  const {
    data: shiftTemplates = [],
    refetch: refetchShiftTemplates,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useGetShiftTemplatesQuery(planningId, {
    refetchOnMountOrArgChange: true,
  })

  const { schedulerData, statistics } = usePlanningCalendar({ currentDate, planning, shifts, shiftExceptions, shiftTemplates, viewMode })

  if (isLoadingTemplates || isLoadingShifts || isLoadingShiftExceptions) return <PlanningCalendarSkeleton />

  if (!!templatesError || !!shiftsError) {
    return (
      <ErrorAlert
        onRefresh={() => {
          refetchShiftTemplates()
          refetchShifts()
        }}
      />
    )
  }
  return (
    <Paper data-testid="planning-calendar" data-view={isMobile ? 'day' : 'week'}>
      {!hideStatistics && <Statistics statistics={statistics} />}
      <PlanningCalendarContext.Provider value={{ planningId, shifts }}>
        <Scheduler data={schedulerData} height="auto" locale={toAdapterLocale(i18n.language)} firstDayOfWeek={PLANNING_WEEK_START_DAY}>
          <ViewState currentDate={currentDate} onCurrentDateChange={setCurrentDate} />
          <Toolbar rootComponent={ToolbarRoot} flexibleSpaceComponent={(props) => <ToolbarFlexibleSpace {...props} viewMode={viewMode} />} />
          <DateNavigator />
          <TodayButton />
          {isMobile ? <DayView {...calendarViewProps} /> : <WeekView {...calendarViewProps} />}
          <Appointments appointmentComponent={Appointment} />
        </Scheduler>
      </PlanningCalendarContext.Provider>
    </Paper>
  )
}

function Appointment(props: Appointments.AppointmentProps) {
  const { planningId, shifts } = usePlanningCalendarContext()
  const data = props.data as CalendarShiftData
  switch (data.type) {
    case 'shift':
      const shift = shifts.find((shift) => shift._id === data.id)
      return shift ? <ShiftAppointment {...props} data={data} shift={shift} planningId={planningId} /> : null
    case 'shiftTemplate':
      return <ShiftTemplateAppointment {...props} data={data} planningId={planningId} />
    default:
      return null
  }
}

function ErrorAlert({ onRefresh }: { onRefresh?: () => void }) {
  const { t } = useTranslation()
  return (
    <Box sx={{ p: 2 }}>
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRefresh}>
            {t('page.planning.calendar.retry', { defaultValue: 'Retry' })}
          </Button>
        }
      >
        {t('error.loadingPlanningDetails')}
      </Alert>
    </Box>
  )
}

function PlanningCalendarSkeleton() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <CircularProgress />
    </Box>
  )
}

function Statistics({ statistics }: { statistics: ReturnType<typeof usePlanningCalendar>['statistics'] }) {
  const { t } = useTranslation()
  const statisticsList: Array<Pick<ChipProps, 'color' | 'icon'> & { label: string; tooltip: string }> = useMemo(
    () => [
      {
        color: 'primary',
        label: `${t('page.planning.calendar.stats.totalShifts')}: ${statistics.totalShifts}`,
        tooltip: t('page.planning.calendar.stats.totalShifts'),
      },
      {
        color: 'success',
        icon: <CheckCircleIcon />,
        label: `${t('page.planning.calendar.stats.filledShifts')}: ${statistics.filledShifts}`,
        tooltip: t('page.planning.calendar.stats.filledShifts'),
      },
      {
        color: 'warning',
        icon: <WarningIcon />,
        label: `${t('page.planning.calendar.stats.partiallyFilledShifts')}: ${statistics.partiallyFilledShifts}`,
        tooltip: t('page.planning.calendar.stats.partiallyFilledShifts'),
      },
      {
        color: 'error',
        icon: <ErrorIcon />,
        label: `${t('page.planning.calendar.stats.emptyShifts')}: ${statistics.emptyShifts}`,
        tooltip: t('page.planning.calendar.stats.emptyShifts'),
      },
      {
        color: 'info',
        label: `${t('page.planning.calendar.stats.fillRate')}: ${statistics.fillRate}%`,
        tooltip: t('page.planning.calendar.stats.fillRate'),
      },
    ],
    [statistics.emptyShifts, statistics.fillRate, statistics.filledShifts, statistics.partiallyFilledShifts, statistics.totalShifts, t]
  )

  return (
    <Stack data-testid="planning-calendar-statistics" direction="row" bgcolor="#f5f5f5" borderTop="1px solid #e0e0e0" flexWrap="wrap" padding={(theme) => theme.spacing(1, 2)} gap={1}>
      {statisticsList.map((item, index) => (
        <StatisticChip key={index} {...item} />
      ))}
    </Stack>
  )
}

function StatisticChip({ color, icon, label, tooltip }: Pick<ChipProps, 'color' | 'icon'> & { label: string; tooltip: string }) {
  return (
    <Tooltip title={tooltip}>
      <Chip color={color} icon={icon} label={label} variant="outlined" sx={{ borderRadius: 4, height: 28 }} />
    </Tooltip>
  )
}

function TimeTableCell(props: WeekView.TimeTableCellProps) {
  return (
    <WeekView.TimeTableCell
      {...props}
      style={{
        backgroundColor: props.isShaded ? '#f5f5f5' : 'white',
        borderLeft: '1px solid #e0e0e0',
        height: 60, // Make cells taller
      }}
    />
  )
}

function DayScaleCell(props: WeekView.DayScaleCellProps) {
  const isToday = dayjs(props.startDate).isSame(dayjs(), 'day')

  return (
    <WeekView.DayScaleCell
      {...props}
      style={{
        backgroundColor: isToday ? '#e3f2fd' : '#f0f8ff',
        fontWeight: 'bold',
        textAlign: 'center',
        borderBottom: isToday ? '2px solid #1976d2' : '1px solid #e0e0e0',
        padding: '8px 0',
      }}
    />
  )
}

function TimeScaleLayout(props: WeekView.TimeScaleLayoutProps) {
  return <WeekView.TimeScaleLayout {...props} formatDate={(date) => dayjs(date).format('HH:mm')} />
}

function TimeScaleLabel(props: WeekView.TimeScaleLabelProps) {
  return (
    <WeekView.TimeScaleLabel
      {...props}
      style={{
        fontWeight: 'bold',
        textAlign: 'right',
        padding: '0 8px',
        color: '#616161',
        height: props.time ? 60 : 30,
        lineHeight: props.time ? '60px' : '30px',
      }}
    />
  )
}

function ToolbarRoot({ children }: Toolbar.RootProps) {
  return <Toolbar.Root style={{ flexWrap: 'wrap', paddingTop: '12px', paddingBottom: '12px' }}>{children}</Toolbar.Root>
}

function ToolbarFlexibleSpace({ viewMode }: Toolbar.FlexibleSpaceProps & { viewMode: string }) {
  const classes = useStyles()
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // The legend is a tall wrapping block; on phones it pushes the calendar grid far down and
  // the appointment cards already convey status, so hide it to save vertical space.
  if (isMobile) return null

  return (
    <Stack direction="row" flexWrap="wrap" spacing={1} py={2}>
      <Typography variant="subtitle2" sx={{ mr: 2, fontWeight: 'bold' }}>
        {t('page.planning.calendar.legend.title')}:
      </Typography>

      {(viewMode === 'all' || viewMode === 'shifts') && (
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Tooltip title={t('page.planning.calendar.legend.fullyStaffed')}>
            <Box className={classes.legendItem}>
              <Box className={classes.colorBox} sx={{ backgroundColor: theme.palette.success.main, border: `2px solid ${theme.palette.success.dark}` }} />
              <Typography variant="caption">{t('page.planning.calendar.legend.fullyStaffed')}</Typography>
            </Box>
          </Tooltip>

          <Tooltip title={t('page.planning.calendar.legend.partiallyStaffed')}>
            <Box className={classes.legendItem}>
              <Box className={classes.colorBox} sx={{ backgroundColor: theme.palette.warning.main, border: `2px solid ${theme.palette.warning.dark}` }} />
              <Typography variant="caption">{t('page.planning.calendar.legend.partiallyStaffed')}</Typography>
            </Box>
          </Tooltip>

          <Tooltip title={t('page.planning.calendar.legend.unstaffed')}>
            <Box className={classes.legendItem}>
              <Box className={classes.colorBox} sx={{ backgroundColor: theme.palette.error.light, border: `2px dashed ${theme.palette.error.main}` }} />
              <Typography variant="caption">{t('page.planning.calendar.legend.unstaffed')}</Typography>
            </Box>
          </Tooltip>

          <Tooltip title={t('page.planning.calendar.legend.past')}>
            <Box className={classes.legendItem}>
              <Box className={classes.colorBox} sx={{ backgroundColor: theme.palette.grey[300], border: `2px solid ${theme.palette.grey[500]}` }} />
              <Typography variant="caption">{t('page.planning.calendar.legend.past')}</Typography>
            </Box>
          </Tooltip>
        </Stack>
      )}

      {(viewMode === 'all' || viewMode === 'templates') && (
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {viewMode === 'all' && <Divider orientation="vertical" flexItem />}

          <Tooltip title={t('page.planning.calendar.legend.template')}>
            <Box className={classes.legendItem}>
              <Box
                className={classes.colorBox}
                sx={{
                  backgroundColor: 'rgba(33, 150, 243, 0.05)',
                  border: '2px dashed #2196F3',
                }}
              />
              <Typography variant="caption">{t('page.planning.calendar.legend.template')}</Typography>
            </Box>
          </Tooltip>

          <Tooltip title={t('page.planning.calendar.legend.templateWithShift')}>
            <Box className={classes.legendItem}>
              <Box
                className={classes.colorBox}
                sx={{
                  backgroundColor: 'rgba(189, 189, 189, 0.15)',
                  border: '2px dashed #9E9E9E',
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.05) 5px, rgba(0,0,0,0.05) 10px)',
                }}
              />
              <Typography variant="caption">{t('page.planning.calendar.legend.templateWithShift')}</Typography>
            </Box>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  )
}
